/**
 * 收藏中心管理Hook
 *
 * 用于初始化FavoritesCenter并设置API客户端
 */

import { useEffect } from "react";
import { useSnapshot } from "valtio";
import { MemberClient } from "../features/api/memberApi";
import { rootStore } from "../store";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { favoritesCenter } from "@/src/store/favorite/FavoritesCenter";

/**
 * Hook to initialize and configure FavoritesCenter
 *
 * Note: This hook should be called as early as possible (e.g., in RootLayoutClient)
 * to ensure setApiClient is called before other components try to use FavoritesCenter APIs.
 *
 * Initialization strategy:
 * 1. Set API client as soon as memberApi is ready (even before login)
 *    This ensures handlers are initialized early
 * 2. Set user ID when ownerInfo?.id and isLoggedIn are ready
 *    This happens after API client is set
 */
export const useFavoritesCenterInit = () => {
  const { memberApi } = useOspApiClientProvider();
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { isLoggedIn } = useSnapshot(rootStore.login);

  // Effect 1: Initialize API client as soon as memberApi is ready
  // This is the critical step that initializes handlers
  // We do this early, even before user login, to ensure handlers are ready
  useEffect(() => {
    if (memberApi) {
      favoritesCenter.setApiClient(memberApi as MemberClient<any>);
    }
  }, [memberApi]);

  // Effect 2: Set user ID when user is logged in (after API client is set)
  // This happens after handlers are already initialized
  useEffect(() => {
    if (ownerInfo?.id && isLoggedIn && memberApi) {
      favoritesCenter.setCurrentUserId(ownerInfo.id);
    }
  }, [ownerInfo?.id, isLoggedIn, memberApi]);
};
